import { useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtErr } from "../../lib/api";
import { Anchor, ArrowRight, MailCheck } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function RequestAccess() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.post("/access-requests", {
        email: email.trim(),
        name: name.trim() || undefined,
        company: company.trim() || undefined,
      });
      setSent(true);
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-zinc-50">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white border border-zinc-200 rounded-lg p-8 space-y-6"
        data-testid="request-access-form"
      >
        <Link to="/" className="flex items-center gap-2" data-testid="request-access-logo">
          <div className="w-7 h-7 rounded-md bg-ink text-white grid place-items-center">
            <Anchor className="h-4 w-4" />
          </div>
          <span className="font-display text-[17px] font-medium">Seavanto</span>
        </Link>

        <div>
          <MailCheck className="h-6 w-6 text-ink mb-3" />
          <h1 className="font-display text-2xl font-medium tracking-tight">Request beta access</h1>
          <p className="text-sm text-zinc-600 mt-1">
            Seavanto is in private beta. Leave your email and our team will review your request.
          </p>
        </div>

        {sent ? (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 space-y-2"
            data-testid="request-access-sent"
          >
            <div>
              Thanks — we've received your request. A confirmation email is on its way to{" "}
              <span className="font-medium">{email}</span>.
            </div>
            <div className="text-emerald-700">
              Once it's approved you'll receive a second email with a link to set your password.
              Please also check your <strong>spam folder</strong>.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="ra-email">Email</Label>
              <Input
                id="ra-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="request-access-email"
              />
            </div>
            <div>
              <Label htmlFor="ra-name">Name <span className="text-zinc-400">(optional)</span></Label>
              <Input
                id="ra-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                data-testid="request-access-name"
              />
            </div>
            <div>
              <Label htmlFor="ra-company">Yacht <span className="text-zinc-400">(optional)</span></Label>
              <Input
                id="ra-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                autoComplete="organization"
                data-testid="request-access-company"
              />
            </div>
            {err && <div className="text-sm text-red-600" data-testid="request-access-error">{err}</div>}
          </div>
        )}

        {!sent && (
          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-ink hover:bg-black"
            data-testid="request-access-submit"
          >
            {busy ? "Sending…" : "Request access"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}

        <div className="text-sm text-zinc-600 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-ink underline" data-testid="request-access-login-link">
            Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
